import { EstimationItem, EstimationCategory } from '../types/estimation-items';

import preparationData from '../data/detailed-estimation-prices/preparation.json';
import demolitionData from '../data/detailed-estimation-prices/demolition.json';
import grosOeuvreData from '../data/detailed-estimation-prices/grosoeuvre.json';
import vrdData from '../data/detailed-estimation-prices/vrd.json';
import isolationData from '../data/detailed-estimation-prices/isolation.json';
import cloisonsData from '../data/detailed-estimation-prices/cloisons.json';
import techniqueData from '../data/detailed-estimation-prices/technique.json';
import sanitaireData from '../data/detailed-estimation-prices/sanitaire.json';
import revetementsData from '../data/detailed-estimation-prices/revetements.json';
import menuiserieData from '../data/detailed-estimation-prices/menuiserie.json';
import finitionsData from '../data/detailed-estimation-prices/finitions.json';
import exterieursData from '../data/detailed-estimation-prices/exterieurs.json';

const categoryMetadata: Record<string, { title: string; description: string }> = {
  preparation: {
    title: 'Préparation & Études',
    description: 'Diagnostics, études et autorisations nécessaires avant le démarrage des travaux'
  },
  demolition: {
    title: 'Démolition',
    description: 'Dépose et démolition des éléments existants'
  },
  'gros-oeuvre': {
    title: 'Gros œuvre',
    description: 'Travaux de structure : maçonnerie, fondations, planchers'
  },
  vrd: {
    title: 'VRD (Voiries et Réseaux Divers)',
    description: 'Raccordements et réseaux extérieurs'
  },
  isolation: {
    title: 'Isolation et Étanchéité',
    description: 'Isolation thermique et phonique, étanchéité'
  },
  cloisons: {
    title: 'Cloisons & Aménagement',
    description: 'Cloisons intérieures et aménagements'
  },
  technique: {
    title: 'Électricité, Chauffage, Ventilation',
    description: 'Installations électriques, chauffage, ventilation, domotique'
  },
  sanitaire: {
    title: 'Sanitaire et Hygiène',
    description: 'Plomberie, sanitaires, équipements de salles de bain'
  },
  revetements: {
    title: 'Revêtements',
    description: 'Revêtements de sols, murs et plafonds'
  },
  menuiserie: {
    title: 'Menuiseries',
    description: 'Portes, fenêtres, volets et fermetures'
  },
  finitions: {
    title: 'Finitions',
    description: 'Peintures, décoration et finitions'
  },
  exterieurs: {
    title: 'Extérieurs',
    description: 'Aménagements extérieurs, terrasses, clôtures'
  }
};

const categoryDataMap: Record<string, Record<string, EstimationItem>> = {
  preparation: preparationData as Record<string, EstimationItem>,
  demolition: demolitionData as Record<string, EstimationItem>,
  'gros-oeuvre': grosOeuvreData as Record<string, EstimationItem>,
  vrd: vrdData as Record<string, EstimationItem>,
  isolation: isolationData as Record<string, EstimationItem>,
  cloisons: cloisonsData as Record<string, EstimationItem>,
  technique: techniqueData as Record<string, EstimationItem>,
  sanitaire: sanitaireData as Record<string, EstimationItem>,
  revetements: revetementsData as Record<string, EstimationItem>,
  menuiserie: menuiserieData as Record<string, EstimationItem>,
  finitions: finitionsData as Record<string, EstimationItem>,
  exterieurs: exterieursData as Record<string, EstimationItem>
};

export class EstimationDataService {
  private static instance: EstimationDataService;

  private constructor() {}

  public static getInstance(): EstimationDataService {
    if (!EstimationDataService.instance) {
      EstimationDataService.instance = new EstimationDataService();
    }
    return EstimationDataService.instance;
  }

  public getCategoryData(categoryId: string): EstimationCategory | null {
    const items = categoryDataMap[categoryId];
    const metadata = categoryMetadata[categoryId];

    if (!items || !metadata) {
      return null;
    }

    return {
      id: categoryId,
      title: metadata.title,
      description: metadata.description,
      items
    };
  }

  public getItem(categoryId: string, itemKey: string): EstimationItem | null {
    const category = this.getCategoryData(categoryId);
    return category?.items[itemKey] || null;
  }

  public getAllCategories(): EstimationCategory[] {
    return Object.keys(categoryDataMap).map(categoryId => {
      const items = categoryDataMap[categoryId];
      const metadata = categoryMetadata[categoryId];

      return {
        id: categoryId,
        title: metadata.title,
        description: metadata.description,
        items
      };
    });
  }

  public getCategoryIds(): string[] {
    return Object.keys(categoryDataMap);
  }

  public groupItemsBySubcategory(categoryId: string): Record<string, Record<string, EstimationItem>> {
    const category = this.getCategoryData(categoryId);
    if (!category) return {};

    const grouped: Record<string, Record<string, EstimationItem>> = {};

    Object.entries(category.items).forEach(([key, item]) => {
      const parts = key.split('_');
      const subcategoryKey = parts[0];

      if (!grouped[subcategoryKey]) {
        grouped[subcategoryKey] = {};
      }

      grouped[subcategoryKey][key] = item;
    });

    return grouped;
  }

  public getSubcategoryTitle(subcategoryKey: string, categoryId: string): string {
    const titleMap: Record<string, Record<string, string>> = {
      'gros-oeuvre': {
        masonry: 'Maçonnerie et structure',
        correctionplanchers: 'Correction des planchers',
        foundation: 'Fondations',
        stairs: 'Escaliers',
        elevator: 'Ascenseurs',
        openings: 'Ouvertures',
        otherworks: 'Autres travaux'
      },
      demolition: {
        elementstoremove: 'Éléments à déposer',
        decaissement: 'Décaissement',
        toitureamiantee: 'Toiture amiantée',
        demolitionlourde: 'Démolition lourde',
        evacuation: 'Évacuation des déchets'
      },
      preparation: {
        diagnostics: 'Diagnostics obligatoires',
        etudes: 'Études techniques',
        autorisations: 'Autorisations et permis',
        gestionprojet: 'Gestion de projet',
        traitements: 'Traitements préventifs'
      },
      vrd: {
        raccordements: 'Raccordements aux réseaux',
        terrassement: 'Terrassement',
        eauxpluviales: 'Gestion des eaux pluviales',
        divers: 'Divers VRD'
      },
      isolation: {
        mursexterieurs: 'Isolation des murs extérieurs',
        combles: 'Isolation des combles',
        planchersbas: 'Isolation des planchers bas',
        cloisonsinterieures: 'Isolation des cloisons intérieures',
        etancheiteair: 'Étanchéité à l\'air',
        etancheitetoiture: 'Étanchéité de toiture',
        toiture: 'Toiture'
      },
      cloisons: {
        cloisons: 'Cloisons intérieures',
        fauxplafonds: 'Faux-plafonds',
        amenagements: 'Aménagements spécifiques'
      },
      technique: {
        electricite: 'Électricité',
        plomberie: 'Plomberie',
        chauffage: 'Chauffage',
        ventilation: 'Ventilation',
        securiteconformite: 'Sécurité et conformité',
        domotique: 'Domotique',
        energiesrenouvelables: 'Énergies renouvelables'
      },
      sanitaire: {
        sallesdebain: 'Salles de bain',
        sanitaires: 'Sanitaires',
        accessibilite: 'Accessibilité PMR',
        carrelage: 'Carrelage',
        mobilier: 'Mobilier sanitaire',
        ventilation: 'Ventilation'
      },
      revetements: {
        preparationsupports: 'Préparation des supports',
        revetementsmuraux: 'Revêtements muraux',
        revetementssols: 'Revêtements de sols',
        plinthes: 'Plinthes',
        escaliers: 'Habillage d\'escaliers',
        amenagements: 'Aménagements'
      },
      menuiserie: {
        menuiseriesexterieures: 'Menuiseries extérieures',
        volets: 'Volets et fermetures',
        menuiseriesinterieures: 'Menuiseries intérieures',
        serrurerie: 'Serrurerie',
        portesgarageportails: 'Portes de garage et portails'
      },
      finitions: {
        cuisine: 'Cuisine',
        menuiseriesdetails: 'Menuiseries et détails',
        eclairage: 'Éclairage',
        nettoyage: 'Nettoyage de fin de chantier'
      },
      exterieurs: {
        facades: 'Façades',
        toituresexterieures: 'Toitures extérieures',
        amenagements: 'Aménagements extérieurs',
        clotures: 'Clôtures',
        portails: 'Portails',
        espacesverts: 'Espaces verts',
        eclairageexterieur: 'Éclairage extérieur',
        piscine: 'Piscine',
        abris: 'Abris de jardin et structures',
        recuperation: 'Récupération d\'eau'
      }
    };

    return titleMap[categoryId]?.[subcategoryKey] || subcategoryKey;
  }
}

export const estimationDataService = EstimationDataService.getInstance();
